import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AdminLayout } from '../layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Plus, Trash2, Loader2, LifeBuoy, MessageSquare, Send, ChevronRight, Globe, Monitor, Search, X, Paperclip,
} from 'lucide-react';
import { toast } from 'sonner';
import { ticketsApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_PILL = {
    open:        'bg-amber-100 text-amber-800 border-amber-200',
    in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
    done:        'bg-emerald-100 text-emerald-800 border-emerald-200',
};
const STATUS_LABEL = {
    open: 'Offen',
    in_progress: 'In Bearbeitung',
    done: 'Erledigt',
};

const DONE_PREVIEW_LIMIT = 5;

function formatDate(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
}

function captureContext() {
    let urlPath = '';
    try {
        const ref = document.referrer || '';
        if (ref && ref.startsWith(window.location.origin)) {
            urlPath = ref.slice(window.location.origin.length);
        } else {
            urlPath = window.location.pathname + window.location.search;
        }
    } catch { /* ignore */ }
    let ua = '';
    try { ua = navigator.userAgent || ''; } catch { /* ignore */ }
    return { url_path: urlPath, user_agent: ua };
}

function StatusPill({ status, className = '' }) {
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md border ${STATUS_PILL[status] || STATUS_PILL.open} ${className}`}
            data-testid={`ticket-status-pill-${status}`}
        >
            {STATUS_LABEL[status] || status}
        </span>
    );
}

function TicketRow({ ticket, onClick }) {
    const commentCount = (ticket.comments || []).length;
    const attCount = (ticket.attachments || []).length;
    const isPortal = ticket.source === 'portal';
    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full text-left border rounded-lg px-4 py-3 bg-card hover:border-primary/50 hover:bg-muted/30 transition-all group"
            data-testid={`ticket-row-${ticket.id}`}
        >
            <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <StatusPill status={ticket.status} />
                        {isPortal && (
                            <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-md border bg-purple-50 text-purple-800 border-purple-200 uppercase tracking-wide" data-testid="ticket-source-portal">
                                Portal
                            </span>
                        )}
                        <span className="font-semibold text-foreground/90 truncate">{ticket.title}</span>
                        {attCount > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title={`${attCount} Anhang/Anhänge`}>
                                <Paperclip className="h-3 w-3" />
                                {attCount}
                            </span>
                        )}
                        {commentCount > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                <MessageSquare className="h-3 w-3" />
                                {commentCount}
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">
                        {ticket.created_by_email} · {formatDate(ticket.created_at)}
                    </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
            </div>
        </button>
    );
}

function TicketSection({ title, status, count, children, badgeTone, testId }) {
    const tones = {
        amber:   'bg-amber-100 text-amber-800',
        blue:    'bg-blue-100 text-blue-800',
        emerald: 'bg-emerald-100 text-emerald-800',
    };
    return (
        <Card data-testid={testId}>
            <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                    <span className={`inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 text-xs font-bold rounded-full ${tones[badgeTone] || tones.amber}`}>
                        {count}
                    </span>
                    {title}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {children}
            </CardContent>
        </Card>
    );
}

function TicketDetailDialog({ ticket, open, onOpenChange, onChanged, currentUser }) {
    const isCreator = ticket && currentUser && ticket.created_by === currentUser.id;
    const isSuperAdmin = currentUser?.role === 'super_admin';
    const canDelete = isCreator || isSuperAdmin;

    const [reply, setReply] = useState('');
    const [posting, setPosting] = useState(false);
    const [statusSaving, setStatusSaving] = useState(null);
    const replyRef = useRef(null);

    useEffect(() => {
        if (ticket) setReply('');
    }, [ticket?.id]);

    if (!ticket) return null;

    const handleReply = async () => {
        if (reply.trim().length === 0) return;
        setPosting(true);
        try {
            await ticketsApi.addComment(ticket.id, reply.trim());
            toast.success('Antwort gesendet');
            setReply('');
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Antwort fehlgeschlagen');
        } finally {
            setPosting(false);
        }
    };

    const handleStatus = async (newStatus) => {
        if (newStatus === ticket.status) return;
        setStatusSaving(newStatus);
        try {
            await ticketsApi.update(ticket.id, { status: newStatus });
            toast.success(`Status: ${STATUS_LABEL[newStatus]}`);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Status-Änderung fehlgeschlagen');
        } finally {
            setStatusSaving(null);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Ticket wirklich löschen?')) return;
        try {
            await ticketsApi.remove(ticket.id);
            toast.success('Ticket gelöscht');
            onChanged?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm('Kommentar löschen?')) return;
        try {
            await ticketsApi.deleteComment(ticket.id, commentId);
            toast.success('Kommentar gelöscht');
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
        }
    };

    const STATUS_BTN = [
        { value: 'open',        label: 'Offen' },
        { value: 'in_progress', label: 'In Bearbeitung' },
        { value: 'done',        label: 'Erledigt' },
    ];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ticket-detail-dialog">
                <DialogHeader>
                    <div className="flex items-start justify-between gap-3 pr-6">
                        <div className="min-w-0 flex-1">
                            <DialogTitle className="text-xl font-bold pr-2 break-words">
                                {ticket.title}
                            </DialogTitle>
                            <DialogDescription className="mt-1">
                                Von {ticket.created_by_email} · {formatDate(ticket.created_at)}
                            </DialogDescription>
                        </div>
                        <StatusPill status={ticket.status} />
                    </div>
                </DialogHeader>

                <div className="space-y-5 pt-2">
                    {/* Problembeschreibung */}
                    <div className="bg-amber-50/60 border border-amber-100 rounded-lg p-4 space-y-2">
                        <div className="text-xs font-semibold text-amber-900/80 uppercase tracking-wide">
                            Problembeschreibung
                        </div>
                        <p className="text-sm text-foreground/90 whitespace-pre-wrap">{ticket.description}</p>
                    </div>

                    {/* Anhänge */}
                    {(ticket.attachments || []).length > 0 && (
                        <div className="border rounded-lg p-3 space-y-2" data-testid="ticket-attachments">
                            <div className="text-xs font-semibold text-foreground/70 flex items-center gap-1.5">
                                <Paperclip className="h-3.5 w-3.5" />
                                Anhänge ({ticket.attachments.length})
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {ticket.attachments.map(att => {
                                    const url = ticketsApi.attachmentUrl(ticket.id, att.id);
                                    return (
                                        <a
                                            key={att.id}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block border rounded-md overflow-hidden hover:border-primary/50 transition-colors group"
                                            data-testid={`ticket-attachment-${att.id}`}
                                            title={att.filename}
                                        >
                                            <img
                                                src={url}
                                                alt={att.filename}
                                                className="w-full h-24 object-cover bg-muted"
                                                loading="lazy"
                                            />
                                            <div className="text-[10px] text-muted-foreground px-1.5 py-1 truncate group-hover:text-primary">
                                                {att.filename}
                                            </div>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Technischer Kontext */}
                    {(ticket.url_path || ticket.user_agent) && (
                        <div className="border rounded-lg p-3 space-y-1.5 text-xs text-muted-foreground" data-testid="ticket-metadata">
                            <div className="font-medium text-foreground/70 mb-1">Technischer Kontext</div>
                            {ticket.url_path && (
                                <div className="flex items-start gap-1.5">
                                    <Globe className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                    <span className="font-mono break-all">{ticket.url_path}</span>
                                </div>
                            )}
                            {ticket.user_agent && (
                                <div className="flex items-start gap-1.5">
                                    <Monitor className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                    <span className="font-mono break-all">{ticket.user_agent}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Verlauf */}
                    {(ticket.comments || []).length > 0 && (
                        <div className="space-y-2.5">
                            <div className="flex items-center gap-2 text-sm font-medium">
                                <MessageSquare className="h-4 w-4" />
                                Verlauf ({ticket.comments.length})
                            </div>
                            {ticket.comments.map(c => {
                                const canDeleteComment = c.user_id === currentUser?.id || isSuperAdmin;
                                return (
                                    <div key={c.id} className="bg-muted/40 rounded-md p-3 text-sm" data-testid={`ticket-comment-${c.id}`}>
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <div className="text-xs">
                                                <span className="font-semibold">{c.user_name}</span>
                                                <span className="text-muted-foreground"> · {formatDate(c.created_at)}</span>
                                            </div>
                                            {canDeleteComment && (
                                                <button
                                                    onClick={() => handleDeleteComment(c.id)}
                                                    className="text-muted-foreground hover:text-red-600 transition-colors"
                                                    title="Kommentar löschen"
                                                    data-testid={`ticket-comment-delete-${c.id}`}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </div>
                                        <p className="whitespace-pre-wrap">{c.body}</p>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Antwort schreiben */}
                    <div className="space-y-2">
                        <Label htmlFor="reply" className="text-sm font-semibold">Antwort schreiben:</Label>
                        <Textarea
                            id="reply"
                            ref={replyRef}
                            rows={4}
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            placeholder="Ihre Nachricht…"
                            maxLength={5000}
                            data-testid="ticket-reply-input"
                        />
                        <Button
                            onClick={handleReply}
                            disabled={posting || reply.trim().length === 0}
                            data-testid="ticket-reply-submit"
                        >
                            {posting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                            Antwort senden
                        </Button>
                    </div>
                </div>

                <DialogFooter className="border-t pt-4 mt-2 flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-muted-foreground">Status ändern:</span>
                        {STATUS_BTN.map(s => {
                            const active = ticket.status === s.value;
                            return (
                                <button
                                    key={s.value}
                                    type="button"
                                    onClick={() => handleStatus(s.value)}
                                    disabled={statusSaving !== null}
                                    className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors disabled:opacity-50 ${
                                        active
                                            ? STATUS_PILL[s.value]
                                            : 'bg-background hover:bg-muted text-foreground border-border'
                                    }`}
                                    data-testid={`ticket-status-set-${s.value}`}
                                >
                                    {statusSaving === s.value ? (
                                        <span className="inline-flex items-center"><Loader2 className="h-3 w-3 mr-1 animate-spin" /> {s.label}</span>
                                    ) : s.label}
                                </button>
                            );
                        })}
                    </div>
                    {canDelete && (
                        <button
                            type="button"
                            onClick={handleDelete}
                            className="px-3 py-1.5 rounded-md text-xs font-medium border border-red-200 text-red-700 bg-background hover:bg-red-50 transition-colors"
                            data-testid="ticket-delete-btn"
                        >
                            <span className="inline-flex items-center gap-1.5">
                                <Trash2 className="h-3.5 w-3.5" />
                                Löschen
                            </span>
                        </button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function AdminTicketsPage() {
    const { user } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ title: '', description: '' });
    const [detailId, setDetailId] = useState(null);
    const [showAllDone, setShowAllDone] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const capturedContext = useMemo(captureContext, [createOpen]);

    const load = async () => {
        setLoading(true);
        try {
            const r = await ticketsApi.list();
            setTickets(r.data);
        } catch {
            toast.error('Tickets konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openTicket = tickets.find(t => t.id === detailId) || null;

    const handleCreate = async () => {
        if (form.title.trim().length < 3 || form.description.trim().length < 3) {
            toast.error('Titel und Beschreibung benötigen mindestens 3 Zeichen');
            return;
        }
        setCreating(true);
        try {
            await ticketsApi.create({ ...form, priority: 'medium', ...capturedContext });
            toast.success('Ticket angelegt');
            setForm({ title: '', description: '' });
            setCreateOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
        } finally {
            setCreating(false);
        }
    };

    const trimmedQuery = searchQuery.trim().toLowerCase();
    const isSearching = trimmedQuery.length > 0;

    const matchesQuery = (t) => {
        if (!isSearching) return true;
        const haystack = [
            t.title,
            t.description,
            t.created_by_email,
            t.created_by_name,
            ...(t.comments || []).map(c => c.body),
            ...(t.comments || []).map(c => c.user_email),
        ].filter(Boolean).join(' \u0001 ').toLowerCase();
        return haystack.includes(trimmedQuery);
    };

    const filtered = tickets.filter(matchesQuery);

    const openTickets       = filtered.filter(t => t.status === 'open');
    const inProgressTickets = filtered.filter(t => t.status === 'in_progress');
    const doneTickets       = filtered.filter(t => t.status === 'done');

    // Beim Suchen alles aufklappen, sonst Limit anwenden
    const visibleDone   = (showAllDone || isSearching) ? doneTickets : doneTickets.slice(0, DONE_PREVIEW_LIMIT);
    const hiddenDoneCnt = doneTickets.length - visibleDone.length;
    const totalMatches  = openTickets.length + inProgressTickets.length + doneTickets.length;

    return (
        <AdminLayout>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="admin-tickets-page">
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold font-heading flex items-center gap-2">
                            <LifeBuoy className="h-6 w-6 text-primary" />
                            Support-Tickets
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Alle gemeldeten Probleme
                        </p>
                    </div>
                    <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="ticket-create-btn">
                                <Plus className="h-4 w-4 mr-1.5" />
                                Neues Ticket
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg" data-testid="ticket-create-dialog">
                            <DialogHeader>
                                <DialogTitle>Neues Ticket</DialogTitle>
                                <DialogDescription>
                                    Beschreiben Sie kurz den Fehler oder die Beobachtung. Super-Admins werden per E-Mail informiert.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-2">
                                <div>
                                    <Label htmlFor="t-title">Titel</Label>
                                    <Input
                                        id="t-title"
                                        value={form.title}
                                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                                        placeholder="Z. B. Buchungsexport zeigt falsche Summe"
                                        maxLength={200}
                                        data-testid="ticket-title-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="t-desc">Beschreibung</Label>
                                    <Textarea
                                        id="t-desc"
                                        rows={5}
                                        value={form.description}
                                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                                        placeholder="Was ist passiert? Welche Schritte führen zum Fehler?"
                                        maxLength={5000}
                                        data-testid="ticket-desc-input"
                                    />
                                </div>
                                {(capturedContext.url_path || capturedContext.user_agent) && (
                                    <div className="bg-muted/40 rounded-md p-2.5 text-[11px] text-muted-foreground space-y-1" data-testid="ticket-context-preview">
                                        <div className="font-medium text-foreground/80">Automatisch hinzugefügter Kontext</div>
                                        {capturedContext.url_path && (
                                            <div className="flex items-start gap-1.5"><Globe className="h-3 w-3 mt-0.5 shrink-0" /><span className="font-mono break-all">{capturedContext.url_path}</span></div>
                                        )}
                                        {capturedContext.user_agent && (
                                            <div className="flex items-start gap-1.5"><Monitor className="h-3 w-3 mt-0.5 shrink-0" /><span className="font-mono break-all line-clamp-2">{capturedContext.user_agent}</span></div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Abbrechen</Button>
                                <Button onClick={handleCreate} disabled={creating} data-testid="ticket-create-submit">
                                    {creating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
                                    Anlegen
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>

                {loading ? (
                    <Card><CardContent className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></CardContent></Card>
                ) : tickets.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center text-sm text-muted-foreground">
                            Keine Tickets vorhanden.
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {/* Suchleiste */}
                        <div className="space-y-1.5">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <Input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Tickets durchsuchen (Titel, Beschreibung, Antworten, E-Mail)…"
                                    className="pl-9 pr-10"
                                    data-testid="ticket-search-input"
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                        aria-label="Suche zurücksetzen"
                                        data-testid="ticket-search-clear"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                            {isSearching && (
                                <p className="text-xs text-muted-foreground pl-1" data-testid="ticket-search-summary">
                                    {totalMatches === 0
                                        ? <>Keine Treffer für „<span className="font-medium">{searchQuery}</span>".</>
                                        : <>{totalMatches} Treffer für „<span className="font-medium">{searchQuery}</span>".</>}
                                </p>
                            )}
                        </div>

                        {/* Offene Tickets — beim Suchen nur anzeigen wenn Treffer */}
                        {(!isSearching || openTickets.length > 0) && (
                            <TicketSection
                                title="Offene Tickets"
                                count={openTickets.length}
                                badgeTone="amber"
                                testId="ticket-section-open"
                            >
                                {openTickets.length === 0 ? (
                                    <p className="text-sm text-muted-foreground py-2 italic">Keine offenen Tickets. 🎉</p>
                                ) : (
                                    openTickets.map(t => (
                                        <TicketRow key={t.id} ticket={t} onClick={() => setDetailId(t.id)} />
                                    ))
                                )}
                            </TicketSection>
                        )}

                        {/* In Bearbeitung – nur wenn welche da */}
                        {inProgressTickets.length > 0 && (
                            <TicketSection
                                title="In Bearbeitung"
                                count={inProgressTickets.length}
                                badgeTone="blue"
                                testId="ticket-section-in_progress"
                            >
                                {inProgressTickets.map(t => (
                                    <TicketRow key={t.id} ticket={t} onClick={() => setDetailId(t.id)} />
                                ))}
                            </TicketSection>
                        )}

                        {/* Erledigte Tickets */}
                        {doneTickets.length > 0 && (
                            <TicketSection
                                title="Erledigte Tickets"
                                count={doneTickets.length}
                                badgeTone="emerald"
                                testId="ticket-section-done"
                            >
                                {visibleDone.map(t => (
                                    <TicketRow key={t.id} ticket={t} onClick={() => setDetailId(t.id)} />
                                ))}
                                {hiddenDoneCnt > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAllDone(true)}
                                        className="w-full py-2 text-center text-sm text-muted-foreground hover:text-primary italic transition-colors"
                                        data-testid="ticket-show-all-done"
                                    >
                                        … und {hiddenDoneCnt} weitere erledigte Ticket{hiddenDoneCnt === 1 ? '' : 's'}
                                    </button>
                                )}
                            </TicketSection>
                        )}
                    </>
                )}
            </div>

            <TicketDetailDialog
                ticket={openTicket}
                open={!!openTicket}
                onOpenChange={(o) => { if (!o) setDetailId(null); }}
                onChanged={load}
                currentUser={user}
            />
        </AdminLayout>
    );
}
